'use client';

/**
 * SPEC-205 — Navbar unificado Profileia.
 *
 * Sustituye los navbars inline que tenían las páginas públicas (home, /paquetes,
 * /plan-de-compensacion, /about) con un solo componente compartido que aplica
 * las 12 decisiones canónicas del Founder (plan "Casa Unificada" 2026-09-13):
 *
 * - 7 ítems de navegación: Inicio · Ecosistema · AI Pro Army · Compensación · Market · Planes · Conecta · Acceder
 *   (decisión #5 del Founder; Conecta added by CONECTA-PR4-FE-PUBLIC-GRID per
 *   SPEC-245 §7.1 — the public directorio de profesionales verificados)
 * - Logo "Profileia" con gradiente Azul Profileia → Violeta Diamante (MANUAL-MARCA v2.0 §2.5)
 * - CTAs: "Acceder" (ghost) + "Comenzar Gratis" (filled bg-[#2563EB])
 * - Dark mode toggle (preservado de Sprint 91 SPEC-177 Task C)
 * - Mobile menu con animación (motion)
 *
 * Sprint 102 (SPEC-205b follow-up): las 4 páginas nuevas ya existen
 * (/ecosistema, /ai-pro-army, /emprendedores-con-producto, /como-funciona).
 * Los enlaces del navbar ahora apuntan a las páginas reales, no a anchors
 * de la home.
 *
 * Uso:
 *   import { Navbar } from '@/components/layout/Navbar';
 *   <Navbar />  // montado en cualquier página pública
 *
 * Notas:
 * - NO se monta en /app, /admin, /seller, /market/comprar/, /sala/ (esos tienen
 *   su propia navegación interna).
 * - El sticky header + backdrop blur replica el comportamiento anterior.
 */

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTheme } from 'next-themes';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NavItem {
  label: string;
  href: string;
  /** Si es anchor de home (#), se hace scroll suave en vez de navegación. */
  isAnchor?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Inicio', href: '/' },
  // Sprint 102 (SPEC-207): /ecosistema ya existe.
  { label: 'Ecosistema', href: '/ecosistema' },
  // Sprint 102 (SPEC-209): /ai-pro-army pública ya existe.
  { label: 'AI Pro Army', href: '/ai-pro-army' },
  { label: 'Compensación', href: '/plan-de-compensacion' },
  // V126-G1 (Founder directive 2026-10-22): link al Market en el navbar.
  // El Market (/market) es el marketplace público de productos digitales +
  // físicos de los emprendedores. Pre-fix solo era accesible vía el CTA
  // "Descubrir el Market" en el Hub de cada usuario — ahora también desde
  // el navbar público para máxima visibilidad.
  { label: 'Market', href: '/market' },
  { label: 'Planes', href: '/paquetes' },
  // CONECTA-PR4-FE-PUBLIC-GRID (SPEC-245 §7.1): /conecta is the public
  // directorio de profesionales verificados. Visitors (no login required)
  // browse the directory by category + location + full-text search + click
  // through to a SEO-crawlable listing detail page (with JSON-LD
  // ProfessionalService schema) before being routed to /:username (Hub).
  { label: 'Conecta', href: '/conecta' },
];

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();

  useEffect(() => setMounted(true), []);

  const scrollTo = (id: string) => {
    if (typeof window === 'undefined') return;
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAnchorClick = (e: React.MouseEvent, href: string) => {
    if (!href.includes('#')) return;
    e.preventDefault();
    const id = href.split('#')[1];
    scrollTo(id);
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5" aria-label="Profileia — Inicio">
          <Image
            src="/logo/isotipo-fullcolor-256x256.png"
            alt="Profileia"
            width={32}
            height={32}
            className="rounded-lg"
          />
          <span className="text-xl font-bold bg-gradient-to-r from-[#2563EB] to-[#8B5CF6] bg-clip-text text-transparent">
            Profileia
          </span>
        </Link>

        {/* Desktop Nav — 7 ítems (decisión #5 Founder; Conecta added by SPEC-245 §7.1) */}
        <div className="hidden md:flex items-center gap-6">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.label}
              href={item.href as Parameters<typeof Link>[0]['href']}
              onClick={(e) => item.isAnchor && handleAnchorClick(e, item.href)}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {item.label}
            </Link>
          ))}
          {/* "Acceder" como ítem de nav + CTA visual */}
          <Button variant="ghost" size="sm" asChild>
            <Link href="/login">Acceder</Link>
          </Button>
          <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90" asChild>
            <Link href="/register">Comenzar Gratis</Link>
          </Button>
          {mounted && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              aria-label="Cambiar tema"
            >
              <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-2">
          {mounted && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              aria-label="Cambiar tema"
            >
              <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menú"
          >
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-lg overflow-hidden"
          >
            <div className="px-4 py-4 space-y-3">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.label}
                  href={item.href as Parameters<typeof Link>[0]['href']}
                  onClick={(e) => item.isAnchor && handleAnchorClick(e, item.href)}
                  className="block w-full text-left py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  {item.label}
                </Link>
              ))}
              <div className="pt-2 space-y-2">
                <Button variant="outline" className="w-full" asChild>
                  <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                    Acceder
                  </Link>
                </Button>
                <Button className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90" asChild>
                  <Link href="/register" onClick={() => setMobileMenuOpen(false)}>
                    Comenzar Gratis
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
